import { useState } from 'react';
import { Button, Card, Field, formatMoney } from '@elysian/ui';
import type { ContactProduct, Product } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';

/**
 * A customer's own price, code and description for items on the price list,
 * used on their invoices when an item is chosen.
 */
export function TheirPrices({ contactId, name }: { contactId: string; name: string }) {
  const { source, can, context } = useSession();
  const base = context?.organisation.base_currency ?? 'EUR';
  const toast = useToast((t) => t.show);
  const [version, setVersion] = useState(0);
  const data = useAsync(async () => {
    const [mine, products] = await Promise.all([source.contactProducts(contactId), source.products()]);
    return { mine, products };
  }, [source, contactId, version], { keep: true });
  const [editing, setEditing] = useState<{ code: string; price: string; their_code: string; their_description: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editable = can('contacts.write');

  if (data.state !== 'ready') return null;
  const { mine, products } = data.data;
  if (products.length === 0 && mine.length === 0) return null;   // no price list, nothing to say
  const listPrice = (code: string) => products.find((p) => p.code === code)?.sales_price ?? null;

  const start = (row?: ContactProduct) => {
    setError(null);
    setEditing({ code: row?.code ?? '', price: row?.price ? Number(row.price).toFixed(2) : '', their_code: row?.their_code ?? '', their_description: row?.their_description ?? '' });
  };
  async function save(clear = false) {
    if (!editing) return;
    setBusy(true);
    setError(null);
    try {
      await source.setContactProduct(contactId, editing.code, clear ? {} : {
        price: editing.price.trim() || null, their_code: editing.their_code.trim() || null, their_description: editing.their_description.trim() || null,
      });
      toast(clear ? `${editing.code}: back to the list price` : `${editing.code} saved for ${name}`);
      setEditing(null);
      setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" padding="18px 0 8px" aria-labelledby="their-prices">
      <div className="el-card-head">
        <h2 id="their-prices" className="el-h2">Their prices and codes</h2>
        {editable && !editing && <Button size="sm" onClick={() => start()}>Add an item</Button>}
      </div>
      {mine.length === 0 && !editing && (
        <p className="el-hint" style={{ margin: '0 20px 8px' }}>{name} pays list prices. Add an item for a contract price, or their own part number or description.</p>
      )}
      {mine.length > 0 && (
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Their prices and codes">
            <thead><tr><th scope="col">Item</th><th scope="col">Their code and description</th><th scope="col" className="el-num">List price</th><th scope="col" className="el-num">Their price</th></tr></thead>
            <tbody>
              {mine.map((m) => (
                <tr key={m.code}>
                  <td>{editable ? <button type="button" className="el-linkbtn" onClick={() => start(m)} aria-label={`Change ${m.code} for ${name}`}>{m.code}</button> : m.code}
                    <div className="el-hint">{m.description}</div></td>
                  <td>{[m.their_code, m.their_description].filter(Boolean).join(' · ') || <span className="el-hint">Ours</span>}</td>
                  <td className="el-num">{m.list_price ? formatMoney(m.list_price, base) : '—'}</td>
                  <td className="el-num">{m.price ? formatMoney(m.price, base) : <span className="el-hint">List</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <form className="el-stack" style={{ gap: 12, padding: '12px 20px 8px' }} onSubmit={(e) => { e.preventDefault(); void save(); }} aria-label="Their price for an item">
          <div className="el-form-grid">
            <Field id="tp-item" label="Item">
              <select id="tp-item" className="el-input" value={editing.code} disabled={mine.some((m) => m.code === editing.code)}
                onChange={(e) => setEditing({ ...editing, code: e.target.value })}>
                <option value="">Choose an item…</option>
                {products.filter((p: Product) => p.code === editing.code || !mine.some((m) => m.code === p.code)).map((p) => (
                  <option key={p.code} value={p.code}>{p.code} · {p.description}</option>
                ))}
              </select>
            </Field>
            <Field id="tp-price" label="Their price" hint={editing.code && listPrice(editing.code) ? `List price ${formatMoney(listPrice(editing.code)!, base)}` : 'Leave blank for the list price'}>
              <input id="tp-price" className="el-input el-num" inputMode="decimal" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
            </Field>
            <Field id="tp-code" label="Their code" hint="Their part number, printed before the description">
              <input id="tp-code" className="el-input" maxLength={40} value={editing.their_code} onChange={(e) => setEditing({ ...editing, their_code: e.target.value })} />
            </Field>
          </div>
          <Field id="tp-desc" label="Their description" hint="Leave blank for ours">
            <input id="tp-desc" className="el-input" value={editing.their_description} onChange={(e) => setEditing({ ...editing, their_description: e.target.value })} />
          </Field>
          <div className="el-row">
            <Button type="submit" variant="primary" disabled={busy || !editing.code}>Save</Button>
            <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            {mine.some((m) => m.code === editing.code) && <Button variant="danger" disabled={busy} onClick={() => void save(true)}>Back to list price</Button>}
          </div>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </form>
      )}
    </Card>
  );
}
